import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Alert24Icon } from '@kep-ui-kit/ui-icons';

import { StyledSubTitle, StyledText } from '@selfserve/react-ui';
import { buttonIds, cardFormTypes } from '@selfserve/react-utils';
import {
  SubscriptionOverview,
  UserBillingInfo,
} from '@selfserve/react-common-data';

import { StyledErrorContainer } from '../feature/billing-details';

const StyledContainer = styled('div')(({ theme }: { theme?: any }) => ({
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '75%',
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

const StyledMethodTitle = styled(StyledSubTitle)(
  ({ theme }: { theme?: any }) => ({
    display: 'flex',
    marginLeft: 'auto',
    textTransform: 'capitalize',
    color: '#006EFA',
    cursor: 'pointer',
    fontSize: '1rem',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '0',
      marginLeft: '0',
    },
    '&.add-payment-method': {
      marginBottom: 0,
    },
  })
);

const StyledCardInfoContainer = styled('div')(({ theme }: { theme?: any }) => ({
  display: 'flex',
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    marginBottom: '15px',
  },
}));

const StyledBillingInfoContainer = styled('div')(
  ({ theme }: { theme?: any }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '15px',
    },
  })
);

const StyledSectionContainer = styled('div')(({ theme }: { theme?: any }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  '&::first-of-type': {
    marginBottom: '34px',
  },
  [theme.breakpoints.down('md')]: {
    border: '1px solid #E5E5E5',
    borderRadius: '8px',
    maxWidth: '100%',
    padding: '20px',
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginBottom: '5px',
    border: '1px solid #E5E5E5',
    borderRadius: '8px',
    padding: '10px',
  },
  '&.error-wrapper': {
    marginBottom: 0,
  },
}));

const StyledCardIcon = styled.img(({ theme }: { theme?: any }) => ({
  width: '46px',
  padding: '6px',
  border: '1px solid #e1e1e1',
  marginRight: theme.spacing(1),
  borderRadius: '6px',
}));

interface BillingDetailsLayoutProps {
  subscriptionOverviewData: SubscriptionOverview;
  country: string;
  handleModalOpen: (activeFormSection: string) => void;
  cardApiError: string;
  billingApiError: string;
}

export const BillingDetailsLayout = ({
  subscriptionOverviewData,
  country,
  handleModalOpen,
  cardApiError,
  billingApiError,
}: BillingDetailsLayoutProps) => {
  const { t } = useTranslation();
  const { billing_info }: { billing_info?: UserBillingInfo } =
    subscriptionOverviewData;
  const hasCreditCard = useMemo(() => !!billing_info?.lastFour, [billing_info]);
  const addressAndCity = useMemo(
    () =>
      [billing_info?.billing_addr1, billing_info?.billing_city]
        .filter((value) => !!value)
        .join(', '),
    [billing_info]
  );

  return (
    <StyledContainer>
      {billing_info ? (
        <>
          <div>
            <StyledSubTitle>
              {t('billing-details.payment-method')}
            </StyledSubTitle>
            <StyledSectionContainer
              className={cardApiError ? 'error-wrapper' : ''}
            >
              {hasCreditCard ? (
                <StyledCardInfoContainer>
                  {cardApiError && (
                    <StyledErrorContainer>
                      <Alert24Icon className={'err-icon'} />
                    </StyledErrorContainer>
                  )}
                  <StyledCardIcon
                    src={`/assets/${billing_info?.type || 'other'}.svg`}
                  />
                  ****{billing_info?.lastFour}
                  {'\xa0\xa0•\xa0\xa0'}
                  {t('billing-details.expires')} {billing_info?.expiry}
                </StyledCardInfoContainer>
              ) : (
                <StyledText style={{ color: '#000', alignSelf: 'flex-start' }}>
                  {t('billing-details.no-payment-method')}
                </StyledText>
              )}
              <StyledMethodTitle
                onClick={() =>
                  handleModalOpen(
                    hasCreditCard
                      ? cardFormTypes.CHANGE_PAYMENT_METHOD
                      : cardFormTypes.ADD_PAYMENT_METHOD
                  )
                }
                id={
                  hasCreditCard
                    ? buttonIds.changePaymentMethod
                    : buttonIds.addPaymentMethod
                }
              >
                {t(
                  `billing-details.${
                    hasCreditCard
                      ? 'change-payment-method'
                      : 'add-payment-method'
                  }`
                )}
              </StyledMethodTitle>
            </StyledSectionContainer>
            <StyledErrorContainer className="err-text">
              {cardApiError}
            </StyledErrorContainer>
          </div>
          <div>
            <StyledSubTitle>
              {t('billing-details.billing-information')}
            </StyledSubTitle>
            <StyledSectionContainer>
              {billingApiError && (
                <StyledErrorContainer>
                  <Alert24Icon className={'err-icon'} />
                </StyledErrorContainer>
              )}
              <StyledBillingInfoContainer>
                <StyledText>{addressAndCity}</StyledText>
                <StyledText>{billing_info?.billing_zip}</StyledText>
                <StyledText>{country}</StyledText>
              </StyledBillingInfoContainer>
              <StyledMethodTitle
                onClick={() => handleModalOpen(cardFormTypes.EDIT_BILLING_INFO)}
                id={buttonIds.changeBillingInformation}
              >
                {t('billing-details.edit-billing-info')}
              </StyledMethodTitle>
            </StyledSectionContainer>
            <StyledErrorContainer className="err-text">
              {billingApiError}
            </StyledErrorContainer>
          </div>
        </>
      ) : (
        <div>
          <StyledSectionContainer>
            {cardApiError ? (
              <StyledErrorContainer className="err-text">
                {cardApiError}
              </StyledErrorContainer>
            ) : (
              <StyledText style={{ color: '#000' }}>
                {t('billing-details.no-billing-info')}
              </StyledText>
            )}
            <StyledMethodTitle
              onClick={() => handleModalOpen(cardFormTypes.ADD_PAYMENT_METHOD)}
              className="add-payment-method"
            >
              {t('billing-details.add-payment-method')}
            </StyledMethodTitle>
          </StyledSectionContainer>
        </div>
      )}
    </StyledContainer>
  );
};
